/* tslint:disable:jsx-no-lambda WI:47652 */
import setSelectedDiagnosticsPanelEntryIndex from '../mutators/setSelectedDiagnosticsPanelEntryIndex';
import { SearchRequestInstrumentation } from 'owa-search-service';
import searchDiagnosticsStore from '../store/store';
import { observer } from 'mobx-react';
import { formatUserTime, userDate } from 'owa-datetime';
import * as React from 'react';
import { Inspector } from 'react-inspector';
import {
    IColumn,
    DetailsList,
    ConstrainMode,
    Selection,
    SelectionMode,
} from 'office-ui-fabric-react/lib/components/DetailsList';
import { CalculatedResourceTimings } from 'owa-analytics';

const styles = require('./SearchDiagnosticsPanel.scss');

interface SearchPanelTopLevelDataPoint {
    Page: number;
    QueryText: string;
    EndToEndJSTime: number | string;
    StartTime: string;
    HttpStatus: number;
}

interface ResourceTimingBreakdown {
    // From startTime to redirectStart
    RedirectStart: number;

    // From startTime to redirectEnd
    RedirectEnd: number;

    // From startTime to fetchStart
    FetchStart: number;

    // From startTime to domainLookupStart
    DomainLookupStart: number;

    // From startTime to domainLookupEnd
    DomainLookupEnd: number;

    // From startTime to connectStart
    ConnectStart: number;

    // From startTime to secureConnectStart
    SecureConnectStart: number;

    // From startTime to connectEnd
    ConnectEnd: number;

    // From startTime to requestStart
    RequestStart: number;

    // From startTime to responseStart
    ResponseStart: number;

    // From startTime to responseEnd
    ResponseEnd: number;
}

interface OwsPrimeCheckMarks {
    RequestProcessTime: number;
    Query3STime: number;
    ResponseProcessTime: number;
}

interface SearchTimingBreakdown {
    ClientJSPreRequestTime: number | string;
    TotalXHRTime: number | string;
    ResourceTimingsW3C: ResourceTimingBreakdown;
    OwsPrimeRequestProcessTime: number | string;
    Query3STime: number | string;
    OwsPrimeResponseProcessTime: number | string;
    ClientCallbackLatency: number | string;
    ClientJSResponseProcessTime: number | string;
    EndToEndJSTime: number | string;
}

interface SearchDiagnosticsInfo {
    ClientSearchProvider: 'ExecuteSearch' | 'SubstrateSearch';
    PageNumber: number;
    SearchDateTime: string;
    SearchBETarget: string;
    SearchFETarget: string;
    SearchBEHttpStatus: string;
    SearchMsEdgeRef: string;
    SearchErrorContent: any;
    SearchTraceID: string;
    ClientBuildNumber: string;
    ClientTimeSentUTC: Date;
    ClientTimeFinishedUTC: Date;
    ClientHttpStatus: number;
    SearchQueryId: string;
    SearchClientRequestID: string;
    SearchClientSessionID: string;
    ClientName: string;
    HttpProtocol: string;
    ClientJSRenderingTime: number | string;
    ErrorResponse: string;
}

interface SearchPanelDetailsDataPoint {
    TimingBreakdown: SearchTimingBreakdown;
    AdditionalDiagnostics: SearchDiagnosticsInfo;
}

@observer
export default class SearchDiagnosticsPanel extends React.Component<{}, {}> {
    constructor(props: {}) {
        super(props);
        // Select top row
        this.selection.setItems(
            searchDiagnosticsStore.searchInstrumentationList.map(searchInstrumentation => ({
                key: searchInstrumentation.SearchClientRequestID,
            }))
        );
        this.selection.setIndexSelected(
            searchDiagnosticsStore.selectedDiagnosticsPanelEntryIndex,
            true /* isSelected */,
            true /* shouldAnchor */
        );
    }

    componentWillUnmount() {
        setSelectedDiagnosticsPanelEntryIndex(0);
    }

    public renderPanel() {
        return <SearchDiagnosticsPanel />;
    }

    private selection = new Selection({
        onSelectionChanged: () => {
            const index = this.selection.getSelectedIndices()[0];
            setSelectedDiagnosticsPanelEntryIndex(index);
        },
        canSelectItem: () => true,
        selectionMode: SelectionMode.single,
        getKey: (item, index) => index.toString(),
    });

    private mapRequestInstrumentationToTopLevelDatapoint(
        searchInstrumentation: SearchRequestInstrumentation
    ): SearchPanelTopLevelDataPoint {
        const searchRequestDatapoint: SearchPanelTopLevelDataPoint = {
            QueryText: searchInstrumentation.PIIData.queryText,
            Page: searchInstrumentation.PageNumber,
            EndToEndJSTime:
                searchInstrumentation.PageNumber == 0
                    ? searchInstrumentation.ClientJSEndToEndTime
                    : 'N/A',
            StartTime: formatUserTime(userDate(searchInstrumentation.ClientTimeSentUTC.getTime())),
            HttpStatus: searchInstrumentation.ClientHttpStatus,
        };

        return searchRequestDatapoint;
    }

    private getResourceTimingsW3C(
        resourceTimingEntry: CalculatedResourceTimings
    ): ResourceTimingBreakdown {
        // Resource timing entry may be null since it's provided async by the browser, so there are no guarantees
        if (!resourceTimingEntry) {
            return null;
        }

        const resourceTimingBreakdown: ResourceTimingBreakdown = {
            RedirectStart: resourceTimingEntry.RdS,
            RedirectEnd: resourceTimingEntry.RdE,
            FetchStart: resourceTimingEntry.FS,
            DomainLookupStart: resourceTimingEntry.DS,
            DomainLookupEnd: resourceTimingEntry.DE,
            ConnectStart: resourceTimingEntry.CS,
            SecureConnectStart: resourceTimingEntry.SCS,
            ConnectEnd: resourceTimingEntry.CE,
            RequestStart: resourceTimingEntry.RqS,
            ResponseStart: resourceTimingEntry.RpS,
            ResponseEnd: resourceTimingEntry.RpE,
        };

        return resourceTimingBreakdown;
    }

    private getClientProcessResponseDelayTime(
        searchInstrumentation: SearchRequestInstrumentation
    ): number | string {
        // Resource timing entry may be null since it's provided async by the browser, so there are no guarantees
        if (searchInstrumentation.ResourceTimingEntry) {
            return (
                searchInstrumentation.ClientNetworkTime -
                searchInstrumentation.ResourceTimingEntry.RpE
            );
        } else {
            return 'Unknown';
        }
    }

    private getTotalXHRTime(searchInstrumentation: SearchRequestInstrumentation): number | string {
        // Resource timing entry may be null since it's provided async by the browser, so there are no guarantees
        if (searchInstrumentation.ResourceTimingEntry) {
            return searchInstrumentation.ResourceTimingEntry.RpE;
        } else {
            return 'Unknown';
        }
    }

    private parseOwsPrimeCheckMarks(checkMarkString: string): OwsPrimeCheckMarks {
        if (!checkMarkString) {
            // Guard against check mark not being sent back by server
            return null;
        }

        const checkMarks = checkMarkString.split('/');

        // Validate that there should be at least 3 checkmarks
        if (checkMarks.length < 3) {
            return null;
        }

        const requestProcessCheckMarkTime = Number(
            checkMarks[0].substring(checkMarks[0].indexOf('=') + 1)
        );
        const query3SCheckMarkTime = Number(
            checkMarks[1].substring(checkMarks[0].indexOf('=') + 1)
        );
        const responseProcessCheckMarkTime = Number(
            checkMarks[2].substring(checkMarks[0].indexOf('=') + 1)
        );

        const owsPrimeCheckMarks: OwsPrimeCheckMarks = {
            RequestProcessTime: requestProcessCheckMarkTime,
            Query3STime: query3SCheckMarkTime - requestProcessCheckMarkTime,
            ResponseProcessTime: responseProcessCheckMarkTime - query3SCheckMarkTime,
        };

        return owsPrimeCheckMarks;
    }

    private mapSearchInstrumentationToDetailsDataPoint(
        searchInstrumentation: SearchRequestInstrumentation
    ): SearchPanelDetailsDataPoint {
        const owsPrimeCheckMarks = this.parseOwsPrimeCheckMarks(
            searchInstrumentation.DiagnosticsCheckpoints
        );
        return {
            TimingBreakdown: {
                ClientJSPreRequestTime:
                    searchInstrumentation.PageNumber == 0
                        ? searchInstrumentation.ClientJSPreRequestTime
                        : 'N/A',
                TotalXHRTime: this.getTotalXHRTime(searchInstrumentation),
                ResourceTimingsW3C: this.getResourceTimingsW3C(
                    searchInstrumentation.ResourceTimingEntry
                ),
                OwsPrimeRequestProcessTime: owsPrimeCheckMarks
                    ? owsPrimeCheckMarks.RequestProcessTime
                    : 'Unknown',
                Query3STime: owsPrimeCheckMarks ? owsPrimeCheckMarks.Query3STime : 'Unknown',
                OwsPrimeResponseProcessTime: owsPrimeCheckMarks
                    ? owsPrimeCheckMarks.ResponseProcessTime
                    : 'Unknown',
                ClientCallbackLatency: this.getClientProcessResponseDelayTime(
                    searchInstrumentation
                ),
                ClientJSResponseProcessTime:
                    searchInstrumentation.PageNumber == 0
                        ? searchInstrumentation.ClientResponseProcessTime
                        : 'N/A',
                EndToEndJSTime:
                    searchInstrumentation.PageNumber == 0
                        ? searchInstrumentation.ClientJSEndToEndTime
                        : 'N/A',
            },
            AdditionalDiagnostics: {
                ClientSearchProvider: searchInstrumentation.ClientSearchProvider,
                PageNumber: searchInstrumentation.PageNumber,
                SearchDateTime: searchInstrumentation.SearchDateTime,
                SearchBETarget: searchInstrumentation.SearchBETarget,
                SearchFETarget: searchInstrumentation.SearchFETarget,
                SearchBEHttpStatus: searchInstrumentation.SearchBEHttpStatus,
                SearchMsEdgeRef: searchInstrumentation.SearchMsEdgeRef,
                SearchErrorContent: searchInstrumentation.SearchErrorContent,
                SearchTraceID: searchInstrumentation.SearchTraceID,
                ClientBuildNumber: searchInstrumentation.ClientBuildNumber,
                ClientTimeSentUTC: searchInstrumentation.ClientTimeSentUTC,
                ClientTimeFinishedUTC: searchInstrumentation.ClientTimeFinishedUTC,
                ClientHttpStatus: searchInstrumentation.ClientHttpStatus,
                SearchQueryId: searchInstrumentation.SearchQueryId,
                SearchClientRequestID: searchInstrumentation.SearchClientRequestID,
                SearchClientSessionID: searchInstrumentation.SearchClientSessionID,
                ClientName: searchInstrumentation.ClientName,
                HttpProtocol: searchInstrumentation.ResourceTimingEntry
                    ? searchInstrumentation.ResourceTimingEntry.P
                    : 'Unknown',
                ClientJSRenderingTime:
                    searchInstrumentation.PageNumber == 0
                        ? searchInstrumentation.ClientJSRenderingTime
                        : 'N/A',
                ErrorResponse: searchInstrumentation.SearchErrorContent
                    ? searchInstrumentation.SearchErrorContent
                    : 'N/A',
            },
        };
    }

    private getTopLevelPanelEntries(): SearchPanelTopLevelDataPoint[] {
        if (searchDiagnosticsStore.searchInstrumentationList.length == 0) {
            return [];
        }

        return searchDiagnosticsStore.searchInstrumentationList.map(searchRequestInstrumentation =>
            this.mapRequestInstrumentationToTopLevelDatapoint(searchRequestInstrumentation)
        );
    }

    private getSelectedDetailsDataPoint(): SearchPanelDetailsDataPoint {
        const index = searchDiagnosticsStore.selectedDiagnosticsPanelEntryIndex;
        const searchInstrumentation = searchDiagnosticsStore.searchInstrumentationList[index];

        return this.mapSearchInstrumentationToDetailsDataPoint(searchInstrumentation);
    }

    render() {
        const topLevelEntries = this.getTopLevelPanelEntries();

        if (!topLevelEntries || topLevelEntries.length == 0) {
            return null;
        }

        const firstEntry = topLevelEntries[0];
        const columns: IColumn[] = [];
        for (const field of Object.keys(firstEntry)) {
            columns.push({
                key: field,
                minWidth: 0,
                name: field,
                fieldName: field,
            });
        }

        return (
            <div className={styles.outerContainer}>
                <div className={styles.headerText}>
                    Click on a search entry below to see details.
                </div>
                <div className={styles.columnWrapper}>
                    <div className={styles.column}>
                        <div className={styles.listContainer}>
                            <DetailsList
                                items={topLevelEntries}
                                columns={columns}
                                compact={true}
                                constrainMode={ConstrainMode.unconstrained}
                                setKey="searchDiagnostics"
                                selection={this.selection}
                                selectionMode={SelectionMode.single}
                                selectionPreservedOnEmptyClick={true}
                                onRenderItemColumn={(item, index, column) => {
                                    return <span>{item[column.key]}</span>;
                                }}
                            />
                        </div>
                    </div>
                    <div className={styles.column}>
                        {searchDiagnosticsStore.selectedDiagnosticsPanelEntryIndex > -1 && (
                            <div>
                                <span className={styles.inspectorHeader}>
                                    Diagnostics breakdown
                                </span>
                                <div className={styles.inspectorContainer}>
                                    <Inspector
                                        data={this.getSelectedDetailsDataPoint()}
                                        expandPaths={['$', '$.TimingBreakdown']}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}
